import { use } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import CareConnectSettingsProviderContext from 'features/care-connect-settings/contexts/care-connect-settings-provider-context';
import { PersonalNumberRange } from 'features/care-connect-settings/domain/models/personal-number-range';

type PersonalNumberValidation = {
  validationRules: {
    lowerBound: {
      required: string;
      validate: (value: number | null | undefined) => string | true;
    };
    upperBound: {
      required: string;
      validate: (value: number | null | undefined) => string | true;
    };
  };
  handleDigitsOnlyChange: (value: string, onChange: (value: number | null) => void) => void;
};

const usePersonalNumberValidation = (personalNumberRange: PersonalNumberRange): PersonalNumberValidation => {
  const { t } = useTranslation('careConnectSettings');
  const { getValues } = useFormContext<PersonalNumberRange>();
  const { personalNumberRangeDetails } = use(CareConnectSettingsProviderContext);
  const { personalNumberRanges } = personalNumberRangeDetails;

  const fieldRequired = t('personalNumberRangeDetails.validationRules.fieldRequired');
  const equalDigitCount = t('personalNumberRangeDetails.validationRules.equalDigitCount');
  const digitsBetweenTwoAndFive = t('personalNumberRangeDetails.validationRules.digitsBetweenTwoAndFive');
  const doesNotStartWithOne = t('personalNumberRangeDetails.validationRules.doesNotStartWithOne');
  const lowerBoundExceedsUpperBound = t('personalNumberRangeDetails.validationRules.lowerBoundExceedsUpperBound');
  const upperBoundBelowLowerBound = t('personalNumberRangeDetails.validationRules.upperBoundBelowLowerBound');
  const boundsCannotBeEqual = t('personalNumberRangeDetails.validationRules.boundsCannotBeEqual');
  const overlapsWithExistingRange = t('personalNumberRangeDetails.validationRules.overlapsWithExistingRange');

  const hasOverlapWithExistingRanges = (lower: number, upper: number): boolean => {
    const otherRanges = personalNumberRanges?.filter((r) => r.key !== personalNumberRange.key) ?? [];
    return otherRanges.some(
      (r) => r.lowerBound !== undefined && r.upperBound !== undefined && lower <= r.upperBound && upper >= r.lowerBound,
    );
  };

  const validateLowerBound = (value: number | null | undefined): string | true => {
    if (value == null) return fieldRequired;

    const upperBound = getValues('upperBound');
    if (upperBound != null && value.toString().length !== upperBound.toString().length) return equalDigitCount;

    const digitCount = value.toString().length;
    if (digitCount < 2 || digitCount > 5) return digitsBetweenTwoAndFive;

    if (value.toString().startsWith('1')) return doesNotStartWithOne;

    if (upperBound != null && value > upperBound) return lowerBoundExceedsUpperBound;
    if (upperBound != null && value === upperBound) return boundsCannotBeEqual;

    if (upperBound != null && hasOverlapWithExistingRanges(value, upperBound)) return overlapsWithExistingRange;

    return true;
  };

  const validateUpperBound = (value: number | null | undefined): string | true => {
    if (value == null) return fieldRequired;

    const lowerBound = getValues('lowerBound');
    if (lowerBound != null && value.toString().length !== lowerBound.toString().length) return equalDigitCount;

    const digitCount = value.toString().length;
    if (digitCount < 2 || digitCount > 5) return digitsBetweenTwoAndFive;

    if (value.toString().startsWith('1')) return doesNotStartWithOne;

    if (lowerBound != null && value < lowerBound) return upperBoundBelowLowerBound;
    if (lowerBound != null && value === lowerBound) return boundsCannotBeEqual;

    if (lowerBound != null && hasOverlapWithExistingRanges(lowerBound, value)) return overlapsWithExistingRange;

    return true;
  };

  const validationRules = {
    lowerBound: {
      required: fieldRequired,
      validate: validateLowerBound,
    },
    upperBound: {
      required: fieldRequired,
      validate: validateUpperBound,
    },
  };

  const handleDigitsOnlyChange = (value: string, onChange: (value: number | null) => void): void => {
    const digits = value.replaceAll(/\D/g, '');
    onChange(digits === '' ? null : Number(digits));
  };

  return { validationRules, handleDigitsOnlyChange };
};

export default usePersonalNumberValidation;
